import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import { Box } from '@mui/system'
import isEmpty from 'lodash.isempty'
import React from 'react'

import { formatRating } from '@js/common/utils'

import { CTAButton } from '@f-fertighaus/applications/Next/components/common/CTAButton'
import {
  HdpNavigationTab,
  useActiveTabs
} from '@f-fertighaus/applications/Next/components/common/navigation/tabs/useActiveTabs'
import { RightLinkArrowIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/RightLinkArrowIcon'
import { shadow } from '@f-fertighaus/applications/Next/config/styles'
import { t } from '@f-fertighaus/applications/Next/public/locales/de'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { ratingLabelByScore } from '@f-fertighaus/applications/Next/utils/ratingLabelByScore'
import { Rating as StarRating } from '@f-fertighaus/components/ReviewRating/SearchResultRating'

export const VendorSection = () => {
  const { state, reviewsActive } = useHouseDetail()
  const { handleTabChange } = useActiveTabs()

  if (!reviewsActive || isEmpty(state.review_data)) return null

  return (
    <VendorSectionContainer>
      <ContentBox>
        <VendorLogoContainer>
          <img
            src={state.vendor_verbose.logo['max-340x100.noauto']}
            alt={t('houseDetailPage.houseOverview.vendorLogoAltText', {
              vendorName: state.vendor_verbose.display_name
            })}
          />
        </VendorLogoContainer>
        <VendorReviewsContainer>
          <Typography
            variant='bodyTitle'
            component='p'
            sx={{ lineHeight: '1.3' }}
            marginBottom={1}>
            {t('houseDetailPage.vendor.reviews-section', {
              vendorName: state.vendor_verbose.display_name
            })}
          </Typography>
          <RatingWrapper>
            <StarRating
              value={state.review_data.avg_rating}
              size={27}
              uniqueName='sidebar-rating'
            />
            <Typography
              variant='mediumTitle'
              component='p'
              marginLeft={1}
              lineHeight={1}>
              {formatRating(state.review_data.avg_rating)}
            </Typography>
          </RatingWrapper>
          <Typography variant='body2' sx={{ textWrap: 'nowrap' }}>
            {t(
              `houseDetailPage.vendor.${
                state.review_data.count_total === 1
                  ? 'reviews-amount-single'
                  : 'reviews-amount'
              }`,
              { amount: state.review_data.count_total }
            )}
            <RatingLabel>
              {t(
                `houseDetailPage.vendor.review-label.${ratingLabelByScore(
                  state.review_data.avg_rating
                )}`
              )}
            </RatingLabel>
          </Typography>
        </VendorReviewsContainer>
      </ContentBox>
      {state.review_data.results.length > 0 && (
        <CtaContainer>
          <CTAButton
            variant='text'
            color='secondary'
            fontWeight='fontWeightRegular'
            endIcon={
              <Box sx={{ transform: 'rotate(90deg)' }}>
                <RightLinkArrowIcon />
              </Box>
            }
            onClick={() => handleTabChange(HdpNavigationTab.vendor)}>
            <Box fontWeight='fontWeightRegular'>
              {t('houseDetailPage.vendor.scroll-to-section')}
            </Box>
          </CTAButton>
        </CtaContainer>
      )}
    </VendorSectionContainer>
  )
}

const VendorSectionContainer = styled(Box)`
  box-shadow: ${shadow.actionCard};
  border-radius: 0 0 var(--border-radius-card-large)
    var(--border-radius-card-large);
  margin: 8px 0 0 0;
  label: VendorSectionContainer;
`

const CtaContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  background: ${({ theme }) => theme.palette.glass[500]};
`

const ContentBox = styled.div`
  display: flex;
  align-items: stretch;
  column-gap: 8px;
  background: ${({ theme }) => theme.palette.common.white};
  padding: 16px 16px 8px 11px;
`

export const VendorLogoContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  width: 110px;
  height: 110px;
  padding: 4px;
  border-radius: var(--border-radius-card-large);
  border: 1px solid ${({ theme }) => theme.palette.stone[50]};
  overflow: hidden;

  & img {
    object-fit: contain;
    object-position: center;
  }
`

const RatingLabel = styled.strong`
  margin-left: 8px;

  &:before {
    content: '·';
    margin-right: 8px;
  }
`

const RatingWrapper = styled.div`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 3px;
`

const VendorReviewsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`
